import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Brain, Sparkles, Bug } from "lucide-react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot, collection, getDocs } from "firebase/firestore";

// 🟢 1. 引入設定與工具
import { auth, db } from "./config/firebase";
import { LOGO_TEXT, DEFAULT_PLANS, DEFAULT_SYS_CONFIG } from "./config/constants";
import { getRemainingTextForMenu } from "./utils/helpers";

// 🟢 2. 引入頁面
import LoginPage from "./pages/LoginPage";
import InputPage from "./pages/InputPage";
import ResultPage from "./pages/ResultPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// 🟢 3. 引入彈窗組件
import UserCenterMenu from "./components/modals/UserCenterMenu";
import NewsModal from "./components/modals/NewsModal";
import HistoryModal from "./components/modals/HistoryModal";
import ReportModal from "./components/modals/ReportModal";
import MemberProfileModal from "./components/modals/MemberProfileModal";
import DailyQuotaModal from "./components/modals/DailyQuotaModal";
import PointsModal from "./components/modals/PointsModal";
import MembershipInfoModal from "./components/modals/MembershipInfoModal";
import ChangePasswordModal from "./components/modals/ChangePasswordModal";
import MyIssuesModal from "./components/modals/MyIssuesModal";

// 自動注入 Tailwind (如果您的環境沒有 index.css 可保留此行，否則可移除)
const useAutoStyles = () => {
  useEffect(() => {
    if (!document.querySelector('script[src*="tailwindcss"]')) {
      const script = document.createElement("script");
      script.src = "https://cdn.tailwindcss.com";
      document.head.appendChild(script);
    }
  }, []);
};

function AppContent() {
  useAutoStyles();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [sysConfig, setSysConfig] = useState(DEFAULT_SYS_CONFIG);
  const [plansMap, setPlansMap] = useState(DEFAULT_PLANS);
  const location = useLocation();

  // --- Modal 開關狀態 ---
  const [showNews, setShowNews] = useState(true);
  const [showMemberProfile, setShowMemberProfile] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDailyQuota, setShowDailyQuota] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [showChangePwdModal, setShowChangePwdModal] = useState(false);
  const [showMyIssuesModal, setShowMyIssuesModal] = useState(false);

  // 監聽登入狀態
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setUserData(null);
        setDataLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // 監聽使用者資料
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, "artifacts", "chengkai-math-app", "public", "data", "registered_users", user.email.toLowerCase());
    const unsub = onSnapshot(ref, (docSnap) => {
      if (docSnap.exists()) {
        setUserData({ id: docSnap.id, ...docSnap.data() });
      }
      setDataLoading(false);
    });
    return () => unsub();
  }, [user]);

  // 監聽系統設定
  useEffect(() => {
    const ref = doc(db, "artifacts", "chengkai-math-app", "public", "data", "system_settings", "config");
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSysConfig(prev => ({ ...prev, ...data }));
        // 強制重整邏輯
        const lastReload = localStorage.getItem("lastForceReload");
        if (data.forceReloadAt && (!lastReload || Number(lastReload) < data.forceReloadAt)) {
            localStorage.setItem("lastForceReload", data.forceReloadAt);
            window.location.reload();
        }
      }
    });
    return () => unsub();
  }, []);

  // 載入會員方案
  useEffect(() => {
    getDocs(collection(db, "artifacts", "chengkai-math-app", "public", "data", "membership_plans")).then(snap => {
      const pm = { ...DEFAULT_PLANS };
      snap.forEach(d => { pm[d.id] = d.data(); });
      setPlansMap(pm);
    });
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/";
  };

  // 核心邏輯：額度檢查 (InputPage 會呼叫這個)
  const checkQuotaAndProceed = (type, callback) => {
    if (!userData) return;
    const planId = (userData.planId || 'A').toUpperCase();
    const plan = plansMap[planId] || plansMap['A'];
    
    let limit = 0, cost = 0, used = 0;
    if (type === 'question') { limit = plan.dailyLimit; cost = sysConfig.pointCostQuestion; used = userData.todaySolved || 0; }
    else if (type === 'quiz') { limit = plan.similarLimit; cost = sysConfig.pointCostQuiz; used = userData.todayQuizzes || 0; }
    else if (type === 'concept') { limit = plan.conceptLimit; cost = sysConfig.pointCostConcept; used = userData.todayConcepts || 0; }

    if (Number(limit) === -1) { callback(false); return; } // 無限暢用

    if (used < limit) {
        callback(false); // 還有免費額度
    } else {
        // 額度用完，檢查點數
        if ((userData.points || 0) >= cost) {
            if (window.confirm(`今日免費額度已用完。\n\n是否同意扣除 ${cost} 點數繼續使用？\n(目前點數: ${userData.points})`)) {
                callback(true); // 使用點數 (isPaid = true)
            }
        } else {
            alert(`今日額度已用完，且點數不足！\n\n需要：${cost} 點\n擁有：${userData.points || 0} 點\n\n請聯絡老師儲值或等待明日重置。`);
        }
    }
  };

  if (dataLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]"><div className="animate-pulse flex flex-col items-center"><Brain size={48} className="text-orange-300 mb-4"/><div className="text-slate-400 font-bold tracking-widest">SYSTEM LOADING...</div></div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0] font-sans text-slate-900 selection:bg-orange-100 selection:text-orange-900">
      
      {/* 🟢 頂部導航列 (Plan A: 手機版懸浮膠囊) */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-500 px-6 py-6 md:px-12 md:py-10 text-white shadow-sm flex-shrink-0 flex flex-col md:flex-row justify-between items-start md:items-center relative z-[100]">
            
        {/* 左側：Logo 與標題 */}
        <div className="flex items-center gap-4 md:gap-6 w-full md:w-auto pr-16 md:pr-0"> 
          <div className="h-12 w-12 md:h-20 md:w-20 rounded-2xl bg-white/20 flex items-center justify-center border-2 border-white/30 shadow-xl backdrop-blur-sm shrink-0">
              <Brain className="text-white w-6 h-6 md:w-9 md:h-9" />
          </div>
          <div>
            <h1 className="text-2xl md:text-4xl font-black tracking-tight mb-1 md:mb-2">{LOGO_TEXT}</h1>
            <div className="flex items-center gap-2 opacity-90">
                <Sparkles className="text-amber-300 w-3 h-3 md:w-4 md:h-4" />
                <p className="text-xs md:text-lg font-medium tracking-wide">AI 智能解題家教 · 幫你把數理變好朋友</p>
            </div>
          </div>
        </div>
        
        {/* 右側：會員中心 (手機版懸浮於右上角) */}
        {userData && (
          <div className="absolute top-6 right-6 md:static md:flex md:flex-col md:items-end md:gap-1 md:mt-0">
            <UserCenterMenu 
              userData={userData}
              plansMap={plansMap}
              onShowPlans={() => setShowPlansModal(true)}
              onLogout={handleLogout}
              onShowProfile={() => setShowMemberProfile(true)}
              onShowQuota={() => setShowDailyQuota(true)}
              onShowPoints={() => setShowPointsModal(true)}
              onShowReport={() => setShowReportModal(true)}
              onShowHistory={() => setShowHistoryModal(true)}
              onChangePwd={() => setShowChangePwdModal(true)}
              getRemainingText={getRemainingTextForMenu}
            />
              {userData.expiryDate ? (
                <span className="hidden md:inline-block text-[10px] font-bold text-white/90 bg-black/10 px-2 py-0.5 rounded-full backdrop-blur-sm">
                    📅 效期至：{userData.expiryDate.toDate().toLocaleDateString()}
                </span>
            ) : (
                <span className="hidden md:inline-block text-[10px] font-bold text-white/60 bg-black/10 px-2 py-0.5 rounded-full backdrop-blur-sm">
                    (持續有效)
                </span>
            )}
          </div>
        )}
      </div>

      {/* 🟢 頁面路由區 */}
      <div className="flex justify-center -mt-6 relative z-10 px-4 pb-20">
        <div className="w-full max-w-5xl bg-white rounded-[1.5rem] md:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col min-h-[85vh] relative z-10">
          <Routes>
            <Route path="/" element={
              user && userData?.status === "approved" 
              ? <InputPage user={user} userData={userData} onCheckQuota={checkQuotaAndProceed} plansMap={plansMap} sysConfig={sysConfig} /> 
              : <LoginPage onLogin={() => {}} sysConfig={sysConfig} />
            } />
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/result" element={
              user && userData?.status === "approved" 
              ? <ResultPage key={location.key} user={user} userData={userData} onCheckQuota={checkQuotaAndProceed} plansMap={plansMap} sysConfig={sysConfig} /> 
              : <Navigate to="/" />
            } />                      
          </Routes>

          {/* 右下角：問題回報浮動按鈕 */}
          {user && userData?.status === "approved" && (
            <button
              onClick={() => setShowReportModal(true)}
              className="fixed bottom-6 right-6 z-[999] w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-xl flex items-center justify-center hover:shadow-2xl hover:scale-105 active:scale-95 transition-all text-slate-700 hover:text-orange-600"
              title="問題回報"
            >
              <Bug size={24} />
            </button>
          )}
        </div>
      </div>

      {/* 🟢 全域彈出視窗 (Modals) */}
      {user && userData?.status === "approved" && showNews && (sysConfig?.announcement || sysConfig?.notices) && location.pathname !== "/login" && (
          <NewsModal sysConfig={sysConfig} onClose={() => setShowNews(false)} />
      )}
      
      {showMemberProfile && <MemberProfileModal userData={userData} onClose={() => setShowMemberProfile(false)} />}
      {showReportModal && <ReportModal user={user} userData={userData} onClose={() => setShowReportModal(false)} onShowHistory={() => { setShowReportModal(false); setShowMyIssuesModal(true); }} />}
      {showHistoryModal && <HistoryModal user={user} onClose={() => setShowHistoryModal(false)} onSelectLog={(log) => { setShowHistoryModal(false); window.location.href = "/result"; /* 這裡簡單處理，實務上可用 navigate 帶 state */ }} />}
      {showDailyQuota && <DailyQuotaModal userData={userData} plansMap={plansMap} onClose={() => setShowDailyQuota(false)} />}
      {showPointsModal && <PointsModal onClose={() => setShowPointsModal(false)} />}
      {showPlansModal && <MembershipInfoModal currentPlanId={(userData?.planId || 'A').toUpperCase()} plansMap={plansMap} onClose={() => setShowPlansModal(false)} />}
      {showChangePwdModal && <ChangePasswordModal userData={userData} onClose={() => setShowChangePwdModal(false)} />}
      {showMyIssuesModal && <MyIssuesModal user={user} onClose={() => setShowMyIssuesModal(false)} />}

    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
